import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';

// Visual representation components for layouts
const LAYOUT_HINT_BLUE = '#e8f0fe';

const LayoutVisual = ({ layoutId, visualIndicator, name }) => {
  const layouts = {
    'hero-centered': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 1 }}>
        <Box sx={{ height: '40%', border: '1px dashed', borderColor: 'primary.main', mb: 0.5 }} />
        <Box display="flex" gap={0.5} height="55%">
          <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'primary.main' }} />
          <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'primary.main' }} />
          <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'primary.main' }} />
        </Box>
      </Box>
    ),
    'split-screen': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', display: 'flex' }}>
        <Box sx={{ flex: 1, bgcolor: LAYOUT_HINT_BLUE, borderRight: '1px solid', borderColor: 'divider' }} />
        <Box sx={{ flex: 1, p: 1 }}>
          <Box sx={{ height: '30%', border: '1px dashed', borderColor: 'primary.main', mb: 0.5 }} />
          <Box sx={{ height: '65%', border: '1px dashed', borderColor: 'primary.main' }} />
        </Box>
      </Box>
    ),
    'video-impact': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', position: 'relative', bgcolor: 'grey.900' }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
          <Box sx={{ width: 40, height: 30, border: '2px solid white', borderRadius: 1 }} />
        </Box>
      </Box>
    ),
    'compact-dense': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ height: 20, border: '1px dashed', borderColor: 'primary.main' }} />
        <Box display="flex" gap={0.5} flex={1}>
          <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'primary.main' }} />
          <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'primary.main' }} />
        </Box>
        <Box sx={{ height: 15, border: '1px dashed', borderColor: 'primary.main' }} />
      </Box>
    ),
    'storytelling': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ height: '30%', border: '1px dashed', borderColor: 'primary.main' }} />
        <Box display="flex" gap={0.5} height="65%">
          <Box sx={{ flex: 2, border: '1px dashed', borderColor: 'primary.main' }} />
          <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'primary.main' }} />
        </Box>
      </Box>
    ),
    'portfolio-grid': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 1 }}>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={0.5} height="100%">
          {[...Array(6)].map((_, i) => (
            <Box key={i} sx={{ border: '1px dashed', borderColor: 'primary.main', bgcolor: i % 2 === 0 ? LAYOUT_HINT_BLUE : 'grey.100' }} />
          ))}
        </Box>
      </Box>
    ),
    'appointment-first': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 1, display: 'flex', gap: 1 }}>
        <Box sx={{ flex: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ height: 14, border: '1px dashed', borderColor: 'primary.main', bgcolor: LAYOUT_HINT_BLUE }} />
          <Box sx={{ flex: 1, border: '2px solid', borderColor: 'primary.main', p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ height: 10, border: '1px dashed', borderColor: 'grey.400' }} />
            <Box sx={{ height: 10, border: '1px dashed', borderColor: 'grey.400' }} />
            <Box sx={{ height: 16, bgcolor: 'primary.main', mt: 'auto' }} />
          </Box>
        </Box>
        <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ height: 14, border: '1px dashed', borderColor: 'grey.400' }} />
          <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'grey.300' }} />
        </Box>
      </Box>
    ),
    'minimal-whitespace': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2 }}>
        <Box sx={{ width: '55%', height: 14, border: '1px solid', borderColor: 'grey.400' }} />
        <Box sx={{ width: '38%', height: 8, border: '1px solid', borderColor: 'grey.300' }} />
        <Box sx={{ width: '28%', height: 18, bgcolor: 'primary.main', mt: 0.5 }} />
      </Box>
    ),
    'testimonial-trust': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mb: 0.5 }}>
          {[...Array(5)].map((_, i) => (
            <Box key={i} sx={{ width: 14, height: 14, bgcolor: '#f59e0b', borderRadius: '2px' }} />
          ))}
        </Box>
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
          {[...Array(2)].map((_, i) => (
            <Box key={i} sx={{ border: '1px dashed', borderColor: 'primary.main', p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ height: 8, border: '1px dashed', borderColor: 'grey.400' }} />
              <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'grey.300' }} />
            </Box>
          ))}
        </Box>
      </Box>
    ),
    'comparison-table': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 0.5 }}>
        <Box sx={{ display: 'flex', height: '100%', gap: 0.5 }}>
          {[...Array(3)].map((_, col) => (
            <Box key={col} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ height: 20, bgcolor: col === 1 ? 'primary.main' : 'grey.200', border: '1px solid', borderColor: col === 1 ? 'primary.main' : 'grey.400' }} />
              {[...Array(4)].map((_, row) => (
                <Box key={row} sx={{ flex: 1, border: '1px dashed', borderColor: col === 1 ? 'primary.main' : 'grey.300', bgcolor: col === 1 ? LAYOUT_HINT_BLUE : 'transparent' }} />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    ),
    'map-location': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '65%', bgcolor: LAYOUT_HINT_BLUE, border: '1px dashed', borderColor: 'primary.main' }}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 12, height: 12, bgcolor: '#ef4444', borderRadius: '50%' }} />
        </Box>
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', p: 0.5, display: 'flex', gap: 0.5 }}>
          <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'grey.400' }} />
          <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'grey.400' }} />
        </Box>
      </Box>
    ),
    'graphic-illustration': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box sx={{ flex: 1, height: '90%', bgcolor: '#fef3c7', border: '1px dashed', borderColor: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ width: 28, height: 28, bgcolor: '#f59e0b', borderRadius: '50%' }} />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ height: 14, border: '1px dashed', borderColor: 'primary.main' }} />
          <Box sx={{ height: 10, border: '1px dashed', borderColor: 'grey.400' }} />
          <Box sx={{ height: 18, bgcolor: '#fef3c7', border: '1px solid', borderColor: '#f59e0b' }} />
        </Box>
      </Box>
    ),
    'data-numbers': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', gap: 0.5, height: '55%' }}>
          {[...Array(3)].map((_, i) => (
            <Box key={i} sx={{ flex: 1, border: '1px solid', borderColor: 'primary.main', p: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Box sx={{ width: 18, height: 18, bgcolor: 'primary.main' }} />
              <Box sx={{ width: '80%', height: 7, border: '1px dashed', borderColor: 'grey.400' }} />
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'grey.300' }} />
      </Box>
    ),
    'product-showcase': (
      <Box sx={{ width: '100%', height: 120, border: '2px solid', borderColor: 'divider', p: 0.5 }}>
        <Box sx={{ height: '28%', border: '1px dashed', borderColor: 'primary.main', mb: 0.5, bgcolor: LAYOUT_HINT_BLUE }} />
        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={0.5} height="66%">
          {[...Array(4)].map((_, i) => (
            <Box key={i} sx={{ border: '1px dashed', borderColor: 'grey.400', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ flex: 1, bgcolor: 'grey.100' }} />
              <Box sx={{ height: 8, bgcolor: 'primary.main', opacity: 0.7 }} />
            </Box>
          ))}
        </Box>
      </Box>
    ),
  };

  return layouts[layoutId] || (
    <Box sx={{
      width: '100%',
      height: 120,
      bgcolor: 'grey.100',
      border: '1px dashed',
      borderColor: 'grey.400',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5
    }}>
      {visualIndicator && (
        <Typography variant="h5" sx={{ lineHeight: 1 }}>{visualIndicator}</Typography>
      )}
      {name && (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', px: 1 }}>{name}</Typography>
      )}
    </Box>
  );
};

const LayoutPicker = ({ value, onChange }) => {
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLayouts();
  }, []);

  const fetchLayouts = async () => {
    try {
      setLoading(true);
      // ✅ FIXED: Changed from '/all' to just '/api/industries/layouts'
      const response = await axios.get('/api/industries/layouts');
      setLayouts(response.data.layouts || []);
      setError(null);
    } catch (err) {
      setError('Failed to load layouts');
      console.error('Error fetching layouts:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Choose Homepage Layout
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select the layout style that best fits your business
      </Typography>

      {/* Selected Layout Display - Shows at top */}
      {value && (
        <Box sx={{ 
          backgroundColor: '#1a3d1a', 
          borderRadius: 2, 
          p: 2, 
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <CheckCircleIcon sx={{ color: '#4ade80', fontSize: 32 }} />
          <Box>
            <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
              Selected: {layouts.find(l => l.id === value)?.name || value}
            </Typography>
            <Typography variant="caption" sx={{ color: '#a3e4a3' }}>
              {layouts.find(l => l.id === value)?.description}
            </Typography>
          </Box>
        </Box>
      )}

      <Grid container spacing={2}>
        {layouts.map((layout) => (
          <Grid item xs={12} sm={6} md={4} key={layout.id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: value === layout.id ? 3 : 1,
                borderColor: value === layout.id ? 'primary.main' : 'divider',
                position: 'relative',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => onChange(layout.id)}
            >
              {value === layout.id && (
                <CheckCircleIcon
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: 32,
                    zIndex: 1
                  }}
                />
              )}
              <CardContent>
                <LayoutVisual layoutId={layout.id} visualIndicator={layout.visualIndicator} name={layout.name} />
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                  {layout.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {layout.description}
                </Typography>
                {layout.bestFor && (
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {layout.bestFor.map((industry, idx) => (
                      <Chip key={idx} label={industry} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
                {layout.characteristics && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {layout.characteristics.join(' • ')}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default LayoutPicker;
